import * as THREE from 'three';
import { events } from '../core/events.js';
import { groundRadius } from './placement.js';
import { getNearestLure, consumeLure } from './throwing.js';

// Small alien grazers — wander the surface, pause to "graze", and scurry
// away when the player gets close. Each creature is a unit-vector position
// on the sphere plus a tangent forward direction; locomotion rotates both
// around their shared cross-product axis so movement stays on the surface.

const UP_Y = new THREE.Vector3(0, 1, 0);
const creatures = [];

// The single carrier of the Circuit Board. Exposed so interactions can
// check proximity + lured-state gating.
let boardCarrier = null;
let carrierSpotted = false;
const SPOT_RANGE = 6.5;            // player distance that first reveals it
const TAKE_RANGE = 1.7;

// Visual + behavioural variants. Size/speed/color vary so the herd reads as
// a mix of species rather than a cloned pack.
const VARIANTS = [
  { color: 0xf0a8c4, accent: 0xb87090, size: 0.45, legs: 6, bodyH: 0.28 }, // pink six-leg
  { color: 0xb4e0c8, accent: 0x5a9c78, size: 0.40, legs: 4, bodyH: 0.26 }, // mint hopper
  { color: 0xcfc0ec, accent: 0x7864a0, size: 0.55, legs: 6, bodyH: 0.32 }, // lavender
];

function makeCreatureMesh(variant) {
  const group = new THREE.Group();

  const bodyMat = new THREE.MeshStandardMaterial({
    color: variant.color, roughness: 0.75, flatShading: true,
  });
  const accentMat = new THREE.MeshStandardMaterial({
    color: variant.accent, roughness: 0.7, flatShading: true,
  });
  const eyeMat = new THREE.MeshStandardMaterial({
    color: 0x121218, roughness: 0.3,
  });
  const shineMat = new THREE.MeshStandardMaterial({
    color: 0xffffff, emissive: 0xffffff, emissiveIntensity: 0.6,
  });

  const s = variant.size;

  // Rounded body — ellipsoid from a scaled icosahedron (flat-shading gives
  // the faceted low-poly feel that fits the rest of the game).
  const body = new THREE.Mesh(new THREE.IcosahedronGeometry(s * 0.5, 1), bodyMat);
  body.scale.set(1.0, variant.bodyH / (s * 0.5) * 0.7, 1.35);
  body.castShadow = true;
  body.position.y = s * 0.32;
  group.add(body);

  // Little dorsal tuft — a pair of small spikes for character.
  for (const [dx, dz] of [[-s * 0.1, -s * 0.05], [s * 0.08, s * 0.05]]) {
    const tuft = new THREE.Mesh(
      new THREE.ConeGeometry(s * 0.06, s * 0.14, 5), accentMat,
    );
    tuft.position.set(dx, s * 0.55, dz);
    tuft.rotation.z = (Math.random() - 0.5) * 0.3;
    group.add(tuft);
  }

  // Eyes — two black beads with a tiny highlight.
  for (const side of [-1, 1]) {
    const eye = new THREE.Mesh(
      new THREE.SphereGeometry(s * 0.08, 10, 8), eyeMat,
    );
    eye.position.set(side * s * 0.18, s * 0.4, s * 0.52);
    group.add(eye);

    const shine = new THREE.Mesh(
      new THREE.SphereGeometry(s * 0.025, 6, 5), shineMat,
    );
    shine.position.set(side * s * 0.16, s * 0.44, s * 0.58);
    group.add(shine);
  }

  // Legs — short cylinders radiating down-and-outward along the body.
  const legCount = variant.legs;
  const rows = legCount / 2;
  for (let r = 0; r < rows; r++) {
    const zFrac = (r / (rows - 1)) - 0.5;       // -0.5 .. 0.5
    for (const side of [-1, 1]) {
      const leg = new THREE.Mesh(
        new THREE.CylinderGeometry(s * 0.05, s * 0.04, s * 0.3, 6),
        accentMat,
      );
      leg.position.set(side * s * 0.26, s * 0.14, zFrac * s * 1.0);
      leg.rotation.z = side * 0.2;
      leg.castShadow = true;
      group.add(leg);
    }
  }

  return group;
}

// Pick a tangent direction at `pos` — starts from an arbitrary world axis,
// removes its radial component, normalises. Falls back to X axis at poles.
function tangentDir(pos) {
  const seed = Math.abs(pos.y) > 0.9
    ? new THREE.Vector3(1, 0, 0)
    : new THREE.Vector3(0, 1, 0);
  return seed.addScaledVector(pos, -seed.dot(pos)).normalize();
}

function randomTangent(pos) {
  const r = new THREE.Vector3().randomDirection();
  r.addScaledVector(pos, -r.dot(pos));
  if (r.lengthSq() < 1e-6) return tangentDir(pos);
  return r.normalize();
}

export function createCreatures(scene, planet, options = {}) {
  const {
    count = 14,
    exclude = null,       // { dir: Vector3, cos: number }  — e.g. pod site
  } = options;

  for (let i = 0; i < count; i++) {
    // Reject directions in the exclusion zone (crash site).
    let dir;
    do {
      dir = new THREE.Vector3().randomDirection();
    } while (exclude && dir.dot(exclude.dir) > exclude.cos);

    const variant = VARIANTS[i % VARIANTS.length];
    const mesh = makeCreatureMesh(variant);
    scene.add(mesh);

    creatures.push({
      mesh,
      planet,
      variant,
      position: dir.clone(),
      forward: randomTangent(dir),
      state: 'idle',
      stateTime: 0.5 + Math.random() * 2.5,
      wanderSpeed: 0.55 + Math.random() * 0.25,
      fleeSpeed: 2.2 + Math.random() * 0.6,
      bobPhase: Math.random() * Math.PI * 2,
      // Lure-puzzle fields (per creature, defaults are harmless).
      isBoardCarrier: false,
      boardMesh: null,
      targetLure: null,
    });
  }

  // Mark one creature as the Circuit Board carrier and give it a visible
  // circuit-board assembly strapped to its back. Multiple parts (flat
  // PCB, raised chip, pinpoint LEDs) so it reads as hardware rather than
  // abstract glow. The lavender variant is the largest, making it easy
  // to spot across the grass.
  const carrier = creatures[creatures.length - 1];   // always a lavender
  const boardRig = new THREE.Group();
  boardRig.position.set(
    0,
    carrier.variant.size * 0.68,
    -carrier.variant.size * 0.02,
  );
  boardRig.rotation.z = -0.08;                       // tilted like a strap

  // Base PCB — darker green so the emissive highlights read against it.
  const pcb = new THREE.Mesh(
    new THREE.BoxGeometry(0.34, 0.03, 0.24),
    new THREE.MeshStandardMaterial({
      color: 0x1f6244, emissive: 0x1f6244, emissiveIntensity: 0.35,
      metalness: 0.2, roughness: 0.6, flatShading: true,
    }),
  );
  pcb.castShadow = true;
  boardRig.add(pcb);

  // Raised central chip — brighter, reads as the "component".
  const chip = new THREE.Mesh(
    new THREE.BoxGeometry(0.14, 0.04, 0.12),
    new THREE.MeshStandardMaterial({
      color: 0x4cd896, emissive: 0x4cd896, emissiveIntensity: 1.2,
      metalness: 0.3, roughness: 0.35, flatShading: true,
    }),
  );
  chip.position.set(0, 0.035, 0);
  chip.castShadow = true;
  boardRig.add(chip);

  // Two pinpoint LEDs (green + amber) to sell "electronics".
  const mkLed = (color, x, z) => {
    const led = new THREE.Mesh(
      new THREE.SphereGeometry(0.02, 10, 8),
      new THREE.MeshStandardMaterial({
        color, emissive: color, emissiveIntensity: 1.4,
      }),
    );
    led.position.set(x, 0.03, z);
    return led;
  };
  boardRig.add(mkLed(0x8cff9c, -0.12, -0.08));
  boardRig.add(mkLed(0xffcf50,  0.12,  0.08));

  // Two black strap slivers across the board so it reads as "tied on".
  const strapMat = new THREE.MeshStandardMaterial({
    color: 0x1a1a20, roughness: 0.9,
  });
  for (const z of [-0.085, 0.085]) {
    const strap = new THREE.Mesh(
      new THREE.BoxGeometry(0.38, 0.02, 0.02), strapMat,
    );
    strap.position.set(0, 0.02, z);
    boardRig.add(strap);
  }

  carrier.mesh.add(boardRig);
  carrier.isBoardCarrier = true;
  carrier.boardMesh = boardRig;
  boardCarrier = carrier;
  carrierSpotted = false;
}

const _axis    = new THREE.Vector3();
const _quat    = new THREE.Quaternion();
const _away    = new THREE.Vector3();
const _target  = new THREE.Vector3();

const FLEE_TRIGGER   = 3.4;     // player-distance (world units) to spook
const FLEE_RELEASE   = 5.0;     // distance at which creature calms again
const LURE_REACHED   = 0.6;     // distance to lure that counts as "arrived"
const EATING_TIME    = 4.5;     // seconds spent feeding at a lure

export function updateCreatures(dt, playerPos) {
  for (const c of creatures) {
    const worldPos = c.mesh.position;
    const distToPlayer = playerPos.distanceTo(worldPos);

    // ---- State transitions ----
    c.stateTime -= dt;

    const busyOnLure = c.state === 'lured' || c.state === 'eating';

    // Lure attraction — overrides idle/wander/flee (food beats fear).
    if (!busyOnLure) {
      const lure = getNearestLure(worldPos);
      if (lure) {
        c.state = 'lured';
        c.stateTime = 12;
        c.targetLure = lure;
        // Face the lure.
        _away.subVectors(lure.worldPos, worldPos);
        _away.addScaledVector(c.position, -_away.dot(c.position));
        if (_away.lengthSq() > 1e-6) c.forward.copy(_away).normalize();
      }
    }

    if (c.state === 'lured') {
      const lure = c.targetLure;
      if (!lure || lure.consumed || lure.dead) {
        c.state = 'idle';
        c.stateTime = 1 + Math.random();
        c.targetLure = null;
      } else {
        // Continuously steer toward the lure on the tangent plane.
        _away.subVectors(lure.worldPos, worldPos);
        _away.addScaledVector(c.position, -_away.dot(c.position));
        if (_away.lengthSq() > 1e-6) c.forward.copy(_away).normalize();
        // Arrived?
        if (lure.worldPos.distanceTo(worldPos) < LURE_REACHED) {
          consumeLure(lure);
          c.state = 'eating';
          c.stateTime = EATING_TIME;
          c.targetLure = null;
        }
      }
    } else if (c.state === 'eating') {
      if (c.stateTime <= 0) {
        c.state = 'idle';
        c.stateTime = 1 + Math.random() * 1.5;
      }
    } else if (c.state !== 'flee' && distToPlayer < FLEE_TRIGGER) {
      c.state = 'flee';
      c.stateTime = 1.2 + Math.random() * 0.8;
      // Face directly away from the player, projected onto the tangent plane.
      _away.copy(c.position).multiplyScalar(worldPos.dot(c.position))
        .sub(playerPos);                          // points from player → creature
      _away.addScaledVector(c.position, -_away.dot(c.position));
      if (_away.lengthSq() > 1e-6) c.forward.copy(_away).normalize();
    } else if (c.stateTime <= 0) {
      if (c.state === 'flee') {
        if (distToPlayer > FLEE_RELEASE) {
          c.state = 'idle';
          c.stateTime = 1.5 + Math.random() * 2.5;
        } else {
          c.stateTime = 0.8;                      // keep running
        }
      } else if (c.state === 'idle') {
        c.state = 'wander';
        c.stateTime = 2 + Math.random() * 4;
        c.forward.copy(randomTangent(c.position));
      } else {
        c.state = 'idle';
        c.stateTime = 1 + Math.random() * 2;
      }
    }

    // First-time spot of the carrier — emits once when the player gets
    // close enough to make out the glowing board on its back.
    if (!carrierSpotted && c.isBoardCarrier && distToPlayer < SPOT_RANGE) {
      carrierSpotted = true;
      events.emit('creatures:carrier-spotted');
    }

    // ---- Movement along the surface ----
    let speed = 0;
    if (c.state === 'wander') speed = c.wanderSpeed;
    else if (c.state === 'flee') speed = c.fleeSpeed;
    else if (c.state === 'lured') speed = c.wanderSpeed * 1.2;

    if (speed > 0) {
      // Rotation axis = position × forward; angle = arc length / radius.
      _axis.crossVectors(c.position, c.forward).normalize();
      const gR = groundRadius(c.planet, c.position);
      const angle = (speed * dt) / gR;
      _quat.setFromAxisAngle(_axis, angle);
      c.position.applyQuaternion(_quat).normalize();
      c.forward.applyQuaternion(_quat);
      // Re-orthogonalise to prevent drift.
      c.forward.addScaledVector(c.position, -c.forward.dot(c.position)).normalize();
    }

    // ---- Position + orientation ----
    const gR = groundRadius(c.planet, c.position);
    worldPos.copy(c.position).multiplyScalar(gR);

    // Look along forward with up = position.
    _target.copy(worldPos).add(c.forward);
    c.mesh.up.copy(c.position);
    c.mesh.lookAt(_target);

    // ---- Bob / bounce ----
    const stepRate =
      c.state === 'flee'   ? 14 :
      c.state === 'wander' ? 7  :
      c.state === 'lured'  ? 8  :
      c.state === 'eating' ? 3  :   // nuzzling the lure
                             1.5;
    const bobAmp =
      c.state === 'flee'   ? 0.09 :
      c.state === 'wander' ? 0.05 :
      c.state === 'lured'  ? 0.06 :
      c.state === 'eating' ? 0.025 :
                             0.015;
    c.bobPhase += dt * stepRate;
    const bob = Math.abs(Math.sin(c.bobPhase)) * bobAmp;
    worldPos.addScaledVector(c.position, bob);
  }
}

// Exposed to the interaction system: returns the carrier entry if it's
// currently easy to approach (lured or eating) and within pickup range.
export function getBoardCarrierInReach(playerWorldPos) {
  if (!boardCarrier || !boardCarrier.isBoardCarrier) return null;
  if (boardCarrier.state !== 'lured' && boardCarrier.state !== 'eating') return null;
  const d = boardCarrier.mesh.position.distanceTo(playerWorldPos);
  if (d > TAKE_RANGE) return null;
  return boardCarrier;
}

// Remove the visible board from the carrier and clear the carry flag.
// Returns true if a board was actually taken.
export function takeBoardFromCarrier() {
  if (!boardCarrier || !boardCarrier.isBoardCarrier) return false;
  if (boardCarrier.boardMesh) {
    boardCarrier.mesh.remove(boardCarrier.boardMesh);
    boardCarrier.boardMesh = null;
  }
  boardCarrier.isBoardCarrier = false;
  return true;
}

// Current unit-vector position of the board carrier on the sphere, or
// null if no carrier exists (e.g. the board has already been taken).
// The dev menu uses this for a "follow the carrier" teleport entry.
export function getBoardCarrierPos() {
  if (!boardCarrier || !boardCarrier.isBoardCarrier) return null;
  return boardCarrier.position.clone();
}
